import { observer } from "mobx-react";
import * as React from "react";
import { jobCategoriesListHeaderOptions } from "../../../constants";
import { useJobCategories } from "../../hooks/useJobCategories";
import { useMobileMediaQuery } from "../../hooks/useMobileMediaQuery";
import { useTableStore } from "../../../stores/TableStore";
import { ContentWrapper } from "../../ui/ContentWrapper";
import { JobCategoriesList } from "../JobCategoriesList";
import { THEME } from "../../../theme/ThemeStore";
import { generalStore } from "../../../stores/GeneralStore";
import { HrBotOrangeButton } from "../../ui/Primitives";
import { t } from "../../../i18n/util";
import { API } from "../../../network/API";
import { JobCategoryCreatePayload, JobCategoryUpdatePayload } from "../../../network/APITypes";
import { JobCategoryEditDialog } from "../dialogs/JobCategoryEditDialog";

export const JobCategoriesSite = observer(() => {
    const isMobile = useMobileMediaQuery();
    const [openAddDialog, setOpenAddDialog] = React.useState(false);
    const [openEditDialog, setOpenEditDialog] = React.useState(false);
    const [selectedJobCategory, setSelectedJobCategory] = React.useState<any>(null);

    const tableStore = useTableStore("jobCategories", {
        orderBy: "createdAt",
        orderDir: "desc"
    });

    const { jobCategories, initialLoaded, reload } = useJobCategories({
        isDal: THEME.THEME_NAME === "dal" ? true : false,
        includeInactive: true
    });

    const handleCloseAddDialog = () => {
        setOpenAddDialog(false);
    };

    const handleCloseEditDialog = () => {
        setOpenEditDialog(false);
        setSelectedJobCategory(null);
    };

    const handleSubmitAddDialog = async (values: JobCategoryCreatePayload | JobCategoryUpdatePayload) => {
        try {
            generalStore.isLoading = true;
            await API.postJobCategory(values as JobCategoryCreatePayload);
            setOpenAddDialog(false);
            reload();
        } catch (error) {
            generalStore.setError(t("error.required"));
        } finally {
            generalStore.isLoading = false;
        }
    };

    const handleSubmitEditDialog = async (values: JobCategoryUpdatePayload) => {
        try {
            generalStore.isLoading = true;
            await API.patchJobCategory(selectedJobCategory.id, values);
            setOpenEditDialog(false);
            setSelectedJobCategory(null);
            reload();
        } catch (error) {
            generalStore.setError(t("error.required"));
        } finally {
            generalStore.isLoading = false;
        }
    };

    const handleEditJobCategory = (jobCategory: any) => {
        setSelectedJobCategory(jobCategory);
        setOpenEditDialog(true);
    };

    const responsiveHeaderOptions = isMobile ? jobCategoriesListHeaderOptions.slice(0, 2) : jobCategoriesListHeaderOptions;
    const tableCellWidthProportionsArray = isMobile ? [2, 1] : [2, 1, 1];

    return (
        <div
            style={{
                padding: isMobile ? 20 : 36,
                flexGrow: 1,
                display: "flex",
                flexDirection: "column"
            }}
        >
            <div
                id="stickyParent"
                style={{
                    display: "flex",
                    position: "relative",
                    flexGrow: 1,
                    overflow: "auto",
                    flexDirection: "column"
                }}
            >
                <ContentWrapper
                    style={
                        !generalStore.hideNavigation
                            ? { padding: isMobile ? 0 : "0 56px", maxWidth: 1600, flexGrow: 1 }
                            : { flexGrow: 1 }
                    }
                >
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            flexGrow: 1,
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: 50,
                            gap: 32
                        }}
                    >
                        <JobCategoriesList
                            jobCategories={jobCategories}
                            tableStore={tableStore}
                            initialLoaded={initialLoaded}
                            responsiveHeaderOptions={responsiveHeaderOptions}
                            tableCellWidthProportionsArray={tableCellWidthProportionsArray}
                            onEdit={handleEditJobCategory}
                        />

                        {tableStore.totalCount !== 0 && <tableStore.Pagination isMobile={isMobile} />}
                    </div>

                    <div
                        style={{
                            marginBottom: 38,
                            display: "flex",
                            justifyContent: "space-between"
                        }}
                    >
                        <HrBotOrangeButton
                            label={t("screen.jobcategories.add")}
                            onClick={() => setOpenAddDialog(true)}
                        />
                    </div>
                </ContentWrapper>
            </div >

            <JobCategoryEditDialog
                isOpen={openAddDialog}
                onBack={handleCloseAddDialog}
                onSubmit={handleSubmitAddDialog}
                isEdit={false}
            />

            <JobCategoryEditDialog
                isOpen={openEditDialog}
                onBack={handleCloseEditDialog}
                onSubmit={handleSubmitEditDialog}
                isEdit={true}
                initialValues={selectedJobCategory}
            />
        </div >
    );
});
